/**
 * Sistem iletisinin (davet) posta durumunun arayüzdeki gösterimi (M7 T2). Kural: gerçekleşmemiş başarı bildirilmez —
 * "sağlayıcı kabul etti" alıcıya teslim demek değildir; kuyruktaki ya da gönderilmemiş ileti "gönderildi" diye gösterilmez.
 */
import { useQuery } from '@tanstack/react-query';
import {
  readyResponseSchema,
  type SystemMailReadiness,
  type SystemMailStatus,
} from '@castinflow/shared';
import type { BadgeTone } from '@castinflow/ui';

/**
 * Platformun sistem postası kipi (`GET /ready` yanıtındaki bilgi alanı; oturumsuz okunur, sır içermez). "Parolamı unuttum"
 * sayfası bununla, ileti gönderilemeyecekse bunu baştan söyler. Bilgi platform düzeyindedir — hiçbir hesabın ya da çalışma
 * alanının varlığına bağlı değildir. Okunamadıysa ya da sunucu alanı yayımlamıyorsa `null` (sayfa varsayımda bulunmaz).
 */
export const SYSTEM_MAIL_READINESS_QUERY_KEY = ['system-mail-readiness'] as const;

export function useSystemMailReadiness() {
  return useQuery({
    queryKey: SYSTEM_MAIL_READINESS_QUERY_KEY,
    queryFn: async (): Promise<SystemMailReadiness | null> => {
      // `/ready` hazır değilken 503 döner ama gövde yine aynı biçimdedir; hata zarfı beklenmez
      const res = await fetch('/api/ready', {
        credentials: 'same-origin',
        headers: { accept: 'application/json' },
      });
      const parsed = readyResponseSchema.safeParse(await res.json());
      return parsed.success ? (parsed.data.systemMail ?? null) : null;
    },
    staleTime: 60_000,
    retry: 1,
  });
}

/** Kullanıcı listesindeki durum rozeti */
export const SYSTEM_MAIL_BADGE_TONE: Record<SystemMailStatus, BadgeTone> = {
  queued: 'info',
  provider_accepted: 'success',
  failed: 'danger',
  not_configured: 'warning',
  uncertain: 'warning',
  sink: 'neutral',
  held: 'warning',
};

/**
 * Davet oluşturulduktan sonraki bildirimin tonu: ileti kuyruktaysa olağan; gönderilmeyeceği ya da gönderilemediği
 * biliniyorsa uyarı (davet kaydı oluştu ama ileti gitmedi).
 */
export function inviteToastTone(
  status: SystemMailStatus | undefined,
): 'success' | 'info' | 'warning' {
  switch (status) {
    case undefined:
    case 'queued':
    case 'provider_accepted':
      return 'success';
    case 'sink':
      return 'info';
    default:
      return 'warning';
  }
}
